// src/features/feeds/components/FeedSourcesSection.tsx

import { useState, useMemo, useCallback } from 'react';
import { Stack, Group, Text, Button, Card, Box } from '@mantine/core';
import { modals } from '@mantine/modals';
import { DndContext, closestCenter, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { IconPlus, IconRss } from '@tabler/icons-react';
import { usePlaylists } from '@/features/playlists/usePlaylists';
import { FeedSourceItem } from './FeedSourceItem';
import { FeedSourceDialog } from './FeedSourceDialog';
import type { FeedSourceData } from '@/lib/types/Feed';
import type { FeedSourceFormSchemaData } from '../schemas/FeedSourceForm.schema';

interface FeedSourcesSectionProps {
  sources: FeedSourceData[];
  onSourcesChange: (sources: FeedSourceData[]) => void;
  feedId: number;
}

export function FeedSourcesSection({ sources, onSourcesChange, feedId }: FeedSourcesSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSource, setEditSource] = useState<FeedSourceData | null>(null);
  const { data: playlistData } = usePlaylists();

  // Build a playlist lookup for display labels
  const playlistMap = useMemo(() => {
    if (!playlistData) return new Map<number, string>();
    const map = new Map<number, string>();
    for (const p of [...(playlistData.customLists ?? []), ...(playlistData.tagLists ?? [])]) {
      const count = 'mediaPositions' in p ? p.mediaPositions.length : null;
      const countLabel = count !== null ? ` (${count} items)` : '';
      map.set(p.listId, `${p.title}${countLabel}`);
    }
    return map;
  }, [playlistData]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sources.findIndex((s) =>
      (s.feedSourceId || `new-${sources.indexOf(s)}`) === active.id
    );
    const newIndex = sources.findIndex((s) =>
      (s.feedSourceId || `new-${sources.indexOf(s)}`) === over.id
    );

    if (oldIndex !== -1 && newIndex !== -1) {
      const reordered = arrayMove(sources, oldIndex, newIndex);
      // Recalculate positions
      const updated = reordered.map((s, i) => ({ ...s, position: i }));
      onSourcesChange(updated);
    }
  }, [sources, onSourcesChange]);

  const handleAddSource = useCallback((data: FeedSourceFormSchemaData) => {
    const newSource: FeedSourceData = {
      feedSourceId: 0, // New source, assigned by backend on save
      feedId,
      listId: data.listId,
      position: sources.length,
      title: data.title,
    };
    onSourcesChange([...sources, newSource]);
  }, [sources, onSourcesChange, feedId]);

  const handleEditSource = useCallback((data: FeedSourceFormSchemaData) => {
    if (!editSource) return;
    const updated = sources.map((s) =>
      s === editSource ? { ...s, title: data.title, listId: data.listId } : s
    );
    onSourcesChange(updated);
    setEditSource(null);
  }, [sources, onSourcesChange, editSource]);

  const handleDeleteSource = useCallback((source: FeedSourceData) => {
    modals.openConfirmModal({
      title: 'Remove Source',
      children: (
        <Text size="sm">
          Remove &ldquo;{source.title}&rdquo; from this feed?
        </Text>
      ),
      labels: { confirm: 'Remove', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        const filtered = sources.filter((s) => s !== source);
        const reindexed = filtered.map((s, i) => ({ ...s, position: i }));
        onSourcesChange(reindexed);
      },
    });
  }, [sources, onSourcesChange]);

  const sortableIds = sources.map((s, i) => s.feedSourceId || `new-${i}`);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={600} size="lg">Content Sources</Text>
        <Button
          leftSection={<IconPlus size={16} />}
          variant="light"
          size="sm"
          onClick={() => { setEditSource(null); setDialogOpen(true); }}
        >
          Add Source
        </Button>
      </Group>

      {sources.length === 0 ? (
        <Card withBorder p="xl">
          <Stack align="center" gap="sm">
            <IconRss size={36} stroke={1.5} opacity={0.4} />
            <Text size="sm" c="dimmed" ta="center">
              No sources yet. Add a playlist source to populate this feed.
            </Text>
            <Button
              variant="light"
              leftSection={<IconPlus size={14} />}
              size="sm"
              onClick={() => { setEditSource(null); setDialogOpen(true); }}
            >
              Add Source
            </Button>
          </Stack>
        </Card>
      ) : (
        <Card withBorder p="sm">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              <Stack gap="xs">
                {sources.map((source, i) => (
                  <FeedSourceItem
                    key={source.feedSourceId || `new-${i}`}
                    source={source}
                    index={i}
                    playlistLabel={playlistMap.get(source.listId) ?? null}
                    onEdit={(s) => { setEditSource(s); setDialogOpen(true); }}
                    onDelete={handleDeleteSource}
                  />
                ))}
              </Stack>
            </SortableContext>
          </DndContext>
          <Text size="xs" c="dimmed" mt="xs" ta="center">
            Drag to reorder
          </Text>
        </Card>
      )}

      <FeedSourceDialog
        opened={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditSource(null); }}
        onSubmit={editSource ? handleEditSource : handleAddSource}
        editSource={editSource}
      />
    </Stack>
  );
}
